/**
 * HorizontalSlider - Custom Dijit which subclasses _SliderBase
 * This creates a Slider in the Horizontal orientation
 * It also computes the preferred-size.
 *
 *  Art of a rendered Horizontal Slider with minor-ticks, major-ticks and major-tick-labels
 * === ||=========
 * | | | | | | | |
 * |   |     |   |
 * 0  25    75  100
 */

define([
    //dojo
    "dojo/_base/declare",
    "dojo/_base/lang",
    "dojo/dom-geometry",
    "dojo/query",

    //custom
    "./_AbstractSlider",
    "./HorizontalRule",
    "./HorizontalRuleLabels"

], function (declare, lang, domGeometry, query, _AbstractSlider, HorizontalRule, HorizontalRuleLabels) {

    return declare(_AbstractSlider, {

        baseClass: "vc-slider vc-horizontalSlider",

        //default width of the track, not the overall slider
        width: 200,
        height: 6,


        _mousePixelCoord: "pageX",
        _pixelCount: "w",
        _startingPixelCoord: "x",

        //the thumb is positioned based on the following
        _thumbPositionParam1: "width",
        _thumbPositionParam2: "left",

        //specifies if the values increase from right-to-left
        _isScaleDirectionReverse: false,

        //for QE purposes
        orientation: "horizontal",

        getRuleLabelConstructor: function () {
           return HorizontalRuleLabels;
        },

        _setWidthAttr: function (width) {
            this.sliderTrackNode.style.width = width + "px";
            this.containerNode.style.width = width + "px";
            this._set("width", width);
        },

        getRuleConstructor: function () {
            return HorizontalRule;
        },

        postCreate: function () {
           this.inherited(arguments);
           this.own(
               this.watch("width", lang.hitch(this, "_adjustThumbPosition")),
               // g1078391
               this.watch("fontSize", lang.hitch(this, "_adjustLabelContainerPositioning")),
               this.watch("fontWeight", lang.hitch(this, "_adjustLabelContainerPositioning")),
               this.watch("fontFamily", lang.hitch(this, "_adjustLabelContainerPositioning")),
               this.watch("fontStyle", lang.hitch(this, "_adjustLabelContainerPositioning"))


           );  //move the thumb when the width changes
        },


        _setMajorTickLabelsAttr: function(majorTickLabels){
            this.inherited(arguments);
            // g1061541
            this._adjustLabelContainerPositioning();

        },

        /**
        * Helper method to adjust the position of each major-tick label's containing div
        * For more details see g1061541
        * @private
        */
        _adjustLabelContainerPositioning: function(){
            // check to handle when major-tick labels are set to empty or
            // when font properties are updated when there are no major-tick labels
            if(this.majorTickLabelDijit){
                var labelContainerNodes = query(".dijitRuleLabelContainerH", this.majorTickLabelDijit.domNode);
                labelContainerNodes.forEach(function(labelContainerNode){
                  var labelPosition = domGeometry.position(labelContainerNode);
                  //  negative margin to push it to the left by half the width of the text content
                  // thereby center-aligning it below the tick
                  var marginLeft = (labelPosition.w/2) * -1;
                  labelContainerNode.style.marginLeft = marginLeft + "px";
                });
            }
        },

        //All methods below this comment are used in computation of preferred size

        _computePreferredWidth: function() {
            var preferredWidth, trackAndThumbWidth, majorTickLabelsWidth;
            trackAndThumbWidth = this.width + this.sliderThumbSize.w; //thumb sticks out half its width on either side, so thumb width + track width is total width
            majorTickLabelsWidth = this._computePreferredWidthOfMajorTickLabels();
            preferredWidth = Math.max(trackAndThumbWidth, majorTickLabelsWidth);
            return preferredWidth;
        },

        /**
         * Calculate OuterX
         * The slider-track might need to be offset to the right for it to be within this new bounds
         * @private
         */
        _computePreferredX: function() {
            var offset = this._adjustTrackAndContainerOffset(), preferredX;
            preferredX = this.location[0] - offset;
            return preferredX;
        },

        /**
         * Calculate height: height calculation is more accurate using co-ordinates than adding up the individual heights, because we would also need to account for any space between these different pieces
         *  Use browser co-ordinates
         *  Ty : topmost component is the track and thumb. the thumb will have the least browser Y
         *  By : below the thumb could be a minor-tick widget, major-tick widget and a major-tick-label widget: take the brwoser Y of the lower-most one and add its height to that Y
         *  By - Ty: is the overall height
         * @param bottomMostWidget
         * @returns {number}
         * @private
         */
        _computePreferredHeight: function(){
            var bottomMostWidget = this._getDomNodeOfWidgetAtEnd(); //this method is in the base class - _AbstractSlider
            //Bottom most widget information required for calculation of Preferred Height and OuterY
            if (bottomMostWidget) {
                //cache for use by computePreferredY
                this._bottomWidgetPosition = domGeometry.position(bottomMostWidget);
            }
            var topY = this.sliderThumbSize.y, bottomY, preferredHeight;
            if (bottomMostWidget) {
               bottomY = this._bottomWidgetPosition.y + this._bottomWidgetPosition.h;
            } else {
               bottomY = this.sliderThumbSize.y + this.sliderThumbSize.h;
            }
            preferredHeight =   bottomY - topY;
            return preferredHeight;
        },

        _computePreferredY: function(){
            var preferredOuterY, bottomY, downwardOffsetFromLocation;
            var sliderTrackSize = domGeometry.position(this.sliderTrackNode);
            //height of the art(ticks/tick-labels) from the bottom of the sliderTrack
            if (this._bottomWidgetPosition) {
               bottomY = this._bottomWidgetPosition.y + this._bottomWidgetPosition.h;
               downwardOffsetFromLocation  = (((this._bottomWidgetPosition.y - sliderTrackSize.y) - sliderTrackSize.h) + this._bottomWidgetPosition.h);
                //clear cache
               this._bottomWidgetPosition = null;
            } else {
                downwardOffsetFromLocation = this.sliderThumbSize.h / 3;
            }
            preferredOuterY = this.location[1] - downwardOffsetFromLocation;
            return preferredOuterY;
        },


        _computePreferredWidthOfMajorTickLabels: function () {
            var totalWidthOfTickLabels = 0;
            if (this.majorTickLabelDijit) {
                //preferredSizeOfTickLabels: if ticklabels are present, calculate their total width
                var tickLabelSpans = query(".dijitRuleLabel", this.containerNode);

                //consider the leftmost tick label position or thumb's whichever is lesser
                var leftMostX = 0;
                var firstTickLabelPosition = domGeometry.position(tickLabelSpans[0]);
                var trackPosition = domGeometry.position(this.sliderTrackNode);
                if (firstTickLabelPosition.x < (trackPosition.x - this.sliderThumbSize.w / 2)) {
                    leftMostX = firstTickLabelPosition.x;
                } else {
                    leftMostX = (trackPosition.x - this.sliderThumbSize.w / 2);
                }
                var lastTickLabelPosition = domGeometry.position(tickLabelSpans[tickLabelSpans.length - 1]);
                totalWidthOfTickLabels = (lastTickLabelPosition.x - leftMostX) + lastTickLabelPosition.w;
            }
            return totalWidthOfTickLabels;
        },

        //when the major-tick-label changes, the  sliderTrack node and containerNode need to be offset
        _adjustTrackAndContainerOffset: function () {
            var tickLabelSpans = query(".dijitRuleLabel", this.containerNode),
                trackPosition,
                offsetTrackAndContainer = this.sliderThumbSize.w / 2;

            if (tickLabelSpans.length === 0) {
                offsetTrackAndContainer = this.sliderThumbSize.w / 2; //7 px  the default
            } else {
                var tickLabelX = [];
                for (var i = 0; i < tickLabelSpans.length; i += 1) {
                    var currentLabel = tickLabelSpans[i];
                    tickLabelX[i] = domGeometry.position(currentLabel).x;
                }
                var minTickLabelX = Math.min.apply(null, tickLabelX);
                trackPosition = domGeometry.position(this.sliderTrackNode);
                //check if the label is extending farther to the left than the width of the track alongwith the thumb
                if (minTickLabelX < (trackPosition.x - this.sliderThumbSize.w / 2)) {
                    offsetTrackAndContainer = trackPosition.x - minTickLabelX;
                }
            }
            this.sliderTrackNode.style.left = offsetTrackAndContainer + "px";
            this.containerNode.style.left = offsetTrackAndContainer + "px";
            return offsetTrackAndContainer;
        }
    });
});